import type { InvoiceFormatSettings, SubcontractorBillLineSetting } from "./invoiceFormatSettings";
import type { ConstructionTimelinePhase, MilestoneTemplate } from "../types";
import { timelineBillLineLabel } from "./invoiceBillLineLabels";

/** Synthetic bill line id when a milestone has no subcontractor_bill checklist item. */
export function syntheticMilestoneBillItemId(milestoneTemplateId: string): string {
  return `msbill:${milestoneTemplateId}`;
}

export function syntheticTimelineBillItemId(timelinePhaseId: string): string {
  return `tlbill:${timelinePhaseId}`;
}

export function isSyntheticMilestoneBillItemId(templateItemId: string): boolean {
  return String(templateItemId ?? "").startsWith("msbill:");
}

export function isTimelineBillLine(row: SubcontractorBillLineSetting): boolean {
  return (
    row.billSource === "timeline" ||
    String(row.milestoneTemplateItemId ?? "").startsWith("tlbill:")
  );
}

export function isMilestoneBillLine(row: SubcontractorBillLineSetting): boolean {
  return !isTimelineBillLine(row);
}

export function milestoneBillLines(lines: SubcontractorBillLineSetting[]): SubcontractorBillLineSetting[] {
  return lines.filter(isMilestoneBillLine);
}

export function timelineBillLines(lines: SubcontractorBillLineSetting[]): SubcontractorBillLineSetting[] {
  return lines.filter(isTimelineBillLine);
}

export function buildMilestoneBillLinesFromMasters(
  milestoneTemplates: MilestoneTemplate[],
  existing: SubcontractorBillLineSetting[] = [],
): SubcontractorBillLineSetting[] {
  const byItemId = new Map(existing.map((row) => [row.milestoneTemplateItemId, row]));
  const out: SubcontractorBillLineSetting[] = [];
  const sortedMilestones = [...milestoneTemplates].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title),
  );

  for (const tpl of sortedMilestones) {
    const billItems = tpl.items.filter((item) => item.kind === "subcontractor_bill");

    if (billItems.length > 0) {
      for (const item of billItems) {
        const prev = byItemId.get(item.id);
        out.push({
          billSource: "milestone",
          milestoneTemplateId: tpl.id,
          milestoneTemplateItemId: item.id,
          milestoneTitle: tpl.title,
          lineLabel: prev?.lineLabel?.trim() || item.label,
          timelinePhaseIds: [],
          timelinePhaseTitles: [],
          qty: prev?.qty ?? 1,
          unit: prev?.unit ?? "LS",
          rate: prev?.rate ?? 0,
          hsnCode: prev?.hsnCode ?? "",
        });
      }
      continue;
    }

    const syntheticId = syntheticMilestoneBillItemId(tpl.id);
    const prev = byItemId.get(syntheticId);
    out.push({
      billSource: "milestone",
      milestoneTemplateId: tpl.id,
      milestoneTemplateItemId: syntheticId,
      milestoneTitle: tpl.title,
      lineLabel: prev?.lineLabel?.trim() || timelineBillLineLabel(tpl.title, "subcontractor"),
      timelinePhaseIds: [],
      timelinePhaseTitles: [],
      qty: prev?.qty ?? 1,
      unit: prev?.unit ?? "LS",
      rate: prev?.rate ?? 0,
      hsnCode: prev?.hsnCode ?? "",
    });
  }

  return out;
}

export function buildTimelineBillLinesFromMasters(
  timelinePhases: ConstructionTimelinePhase[],
  existing: SubcontractorBillLineSetting[] = [],
): SubcontractorBillLineSetting[] {
  const byItemId = new Map(existing.map((row) => [row.milestoneTemplateItemId, row]));
  const out: SubcontractorBillLineSetting[] = [];
  const sortedPhases = [...timelinePhases].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title),
  );

  for (const phase of sortedPhases) {
    const phaseId = String(phase.id);
    const syntheticId = syntheticTimelineBillItemId(phaseId);
    const prev = byItemId.get(syntheticId);
    out.push({
      billSource: "timeline",
      milestoneTemplateId: "",
      milestoneTemplateItemId: syntheticId,
      milestoneTitle: phase.title,
      lineLabel: prev?.lineLabel?.trim() || timelineBillLineLabel(phase.title, "subcontractor"),
      timelinePhaseIds: [phaseId],
      timelinePhaseTitles: [phase.title],
      qty: prev?.qty ?? 1,
      unit: prev?.unit ?? "LS",
      rate: prev?.rate ?? 0,
      hsnCode: prev?.hsnCode ?? "",
    });
  }

  return out;
}

export function buildSubcontractorBillLinesFromMasters(
  milestoneTemplates: MilestoneTemplate[],
  timelinePhases: ConstructionTimelinePhase[],
  existing: SubcontractorBillLineSetting[] = [],
): SubcontractorBillLineSetting[] {
  return [
    ...buildMilestoneBillLinesFromMasters(milestoneTemplates, existing),
    ...buildTimelineBillLinesFromMasters(timelinePhases, existing),
  ];
}

export function resolveSubcontractorBillLine(
  settings: InvoiceFormatSettings,
  templateItemId: string,
): SubcontractorBillLineSetting | undefined {
  return settings.subcontractorBillLines.find((row) => row.milestoneTemplateItemId === templateItemId);
}

export function applySubcontractorBillFormatToLine<
  T extends {
    templateItemId: string;
    lineLabel: string;
    lineUnit: string;
    eligibleQty: number;
    rate: number;
    hsnCode: string;
  },
>(line: T, settings: InvoiceFormatSettings): T {
  const cfg = resolveSubcontractorBillLine(settings, line.templateItemId);
  if (!cfg) return line;
  return {
    ...line,
    lineLabel: cfg.lineLabel.trim() || line.lineLabel,
    lineUnit: cfg.unit.trim() || line.lineUnit,
    eligibleQty: cfg.qty > 0 ? cfg.qty : line.eligibleQty,
    rate: cfg.rate > 0 ? cfg.rate : line.rate,
    hsnCode: cfg.hsnCode.trim() || line.hsnCode,
  };
}

export function milestonesForSubcontractorInvoicing(templates: MilestoneTemplate[]): MilestoneTemplate[] {
  return [...templates].sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title));
}

/** @deprecated use milestonesForSubcontractorInvoicing */
export function milestonesWithSubcontractorBills(templates: MilestoneTemplate[]): MilestoneTemplate[] {
  return milestonesForSubcontractorInvoicing(templates);
}

export function milestonesForTimelinePhase(
  templates: MilestoneTemplate[],
  timelinePhaseId: string,
): MilestoneTemplate[] {
  const phaseId = String(timelinePhaseId).trim();
  if (!phaseId) return [];
  return milestonesForSubcontractorInvoicing(templates).filter((tpl) =>
    (tpl.linkedConstructionTimelinePhaseIds ?? []).some((id) => String(id) === phaseId),
  );
}
